import { useState, useEffect, useRef } from 'react';
import { ZoomIn, X } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { propertyData } from '../mock';

export const LuxuryGallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const sectionRef = useRef(null);

  const categories = [
    { id: 'exterior', label: 'Architecture' },
    { id: 'floorplan', label: 'Floorplan' }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll('.gallery-item');
            items.forEach((item, index) => {
              setTimeout(() => {
                item.classList.add('animate-in');
              }, index * 60);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [activeFilter]);

  return (
    <section id="gallery" ref={sectionRef} className="relative py-32 bg-gradient-to-b from-black via-gray-dark to-black">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="font-display text-6xl md:text-7xl font-bold text-white mb-6">
            Visual <span className="gradient-gold">Journey</span>
          </h2>
          <p className="font-garamond text-xl text-gray-400">Immerse yourself in unparalleled beauty</p>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-16">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveFilter(cat.id)}
              className={`font-sans px-8 py-4 rounded-full transition-all duration-500 ${
                activeFilter === cat.id
                  ? 'bg-gradient-to-r from-gold-primary to-gold-dark text-black font-semibold shadow-2xl shadow-gold-primary/30'
                  : 'bg-white/5 text-gray-400 border border-gold-primary/20 hover:border-gold-primary/50 hover:text-gold-primary'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Masonry Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {

      activeFilter === 'floorplan'?
      propertyData.floorPlans.towers.map((image, index) => (      
<div
              key={image.id}
              className={`gallery-item opacity-0 transform translate-y-10 ${
                index % 4 === 0 ? 'md:row-span-2' : ''
              }`}
              onClick={() => setSelectedImage(image)}
            >
              <div className="group relative h-full cursor-pointer overflow-hidden rounded-xl">
                {/* Image */}
                <img
                  src={image.image}
                  alt={image.name}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                />

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="transform scale-0 group-hover:scale-100 transition-transform duration-500">
                      <div className="w-16 h-16 border-2 border-gold-primary rounded-full flex items-center justify-center">
                        <ZoomIn className="w-8 h-8 text-gold-primary" />
                      </div>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <p className="font-sans text-white font-semibold">{image.alt}</p>
                  </div>
                </div>

                {/* Golden Border Effect */}
                <div className="absolute inset-0 border-2 border-transparent group-hover:border-gold-primary/60 rounded-xl transition-all duration-500"></div>
              </div>
            </div>)):
      propertyData.gallery.map((image, index) => (
            <div
              key={image.id}
              className={`gallery-item opacity-0 transform translate-y-10 ${
                index % 4 === 0 ? 'md:row-span-2' : ''
              }`}
              onClick={() => setSelectedImage(image)}
            >
              <div className="group relative h-full cursor-pointer overflow-hidden rounded-xl">
                {/* Image */}
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                />

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="transform scale-0 group-hover:scale-100 transition-transform duration-500">
                      <div className="w-16 h-16 border-2 border-gold-primary rounded-full flex items-center justify-center">
                        <ZoomIn className="w-8 h-8 text-gold-primary" />
                      </div>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <p className="font-sans text-white font-semibold">{image.alt}</p>
                  </div>
                </div>

                {/* Golden Border Effect */}
                <div className="absolute inset-0 border-2 border-transparent group-hover:border-gold-primary/60 rounded-xl transition-all duration-500"></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-7xl bg-black/95 backdrop-blur-2xl border-2 border-gold-primary/30 p-0">
          {selectedImage && (
            <div className="relative">
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-6 right-6 z-10 w-14 h-14 bg-gold-primary hover:bg-gold-dark rounded-full flex items-center justify-center transition-all duration-300 shadow-2xl"
              >
                <X className="w-7 h-7 text-black" />
              </button>
              <img
                src={selectedImage.url}
                alt={selectedImage.alt}
                className="w-full h-auto"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <style>{`
        .gallery-item.animate-in {
          opacity: 1;
          transform: translateY(0);
          transition: opacity 0.7s ease-out, transform 0.7s ease-out;
        }
      `}</style>
    </section>
  );
};
